import styled from "styled-components";
import { fluid } from "@/styles/utils/fluid";
import { PrimaryButton } from "@/styles/Styled.Buttons";
import {
  TypoOverline2,
  TypoCaptionStyles,
  TypoLabelStyles,
  TypoBodyStyles,
} from "@/styles/Styled.Typography";

export const FormCard = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  border-radius: 32px;
  border: 4px solid ${({ theme }) => theme.colors.onyx};
  &::before {
    content: "";
    background-color: ${({ theme }) => theme.colors.white};
    outline: 4px solid ${({ theme }) => theme.colors.white};
    position: absolute;
    border-radius: 24px;
    top: 4px;
    left: 4px;
    right: 4px;
    bottom: 4px;
    z-index: -1;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='24' ry='24' stroke='%23333' stroke-width='2' stroke-dasharray='8%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
  }
  legend {
    position: absolute;
    top: -16px;
    left: 50%;
    z-index: 10;
    transform: translateX(-50%) rotate(-4deg);
  }
  fieldset {
    border: 0;
    padding: 0;
    width: 100%;
  }
  small {
    width: 100%;
    text-align: center;
  }
  p {
    &:last-child {
      margin-bottom: 0;
    }
  }
  h3 {
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translateX(-50%) rotate(-4deg);
  }
  padding: ${({ theme }) =>
    fluid({
      minSize: theme.spacing.card.xs,
      maxSize: theme.spacing.card.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })};
`;

export const FormInputText = styled.p`
  ${TypoBodyStyles}
  color: ${({ theme }) => theme.colors.phillipineSilver};
  background-color: ${({ theme }) => theme.colors.white};
  text-transform: capitalize;
`;

export const FormButton = styled(PrimaryButton)`
  width: 100%;
  @media ${({ theme }) => theme.breakpoints.md} {
    width: auto;
  }
  @media ${({ theme }) => theme.breakpoints.xl} {
    width: auto;
  }
`;

export const ModulePin = styled(TypoOverline2)`
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.onyx};
  padding: 8px;
  border-radius: 8px;
  width: min-content;
  white-space: nowrap;
`;

export const GridFormWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;
  min-height: 0;
  min-width: 0; /* NEW; needed for Firefox */
  &:last-of-type {
    border-bottom: none;
  }
  gap: ${({ theme }) => `
    ${fluid({
      minSize: theme.spacing.formGap.xs,
      maxSize: theme.spacing.formGap.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })}
    0
  `};
`;

export const GridItemFormWrapper = styled.div`
  padding: 0;
  grid-row: auto;
  &:nth-of-type(2) {
    justify-self: center;
    width: 90%;
  }
  small {
    ${TypoCaptionStyles}
    font-weight: ${({ theme }) => theme.fontWeight.regular};
    width: 100%;
    display: flex;
  }
`;

export const GridForm = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;
  min-height: 0;
  min-width: 0; /* NEW; needed for Firefox */
  &:last-of-type {
    border-bottom: none;
  }
  gap: ${({ theme }) => `
    ${fluid({
      minSize: theme.spacing.formGap.xs,
      maxSize: theme.spacing.formGap.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })}
    0
  `};
  grid-template-areas:
    "title"
    "description"
    "input"
    "button";
  /* md and up */
  @media ${({ theme }) => theme.breakpoints.lg},
    /* lg to xl */ ${({ theme }) => theme.breakpoints.xl} {
    /* If $cols contains the 2-column flag, use a two column layout; otherwise stay single column */
    grid-template-columns: ${({ $cols = [] }) =>
      $cols.includes("$gForm2Col") ? "1fr auto" : "1fr"};
    grid-template-areas: ${({ $cols = [] }) =>
      $cols.includes("$gForm2Col")
        ? `"title title"
           "description description"
           "input button"`
        : `"title"
           "description"
           "input"
           "button"`};
    gap: ${({ theme }) =>
      fluid({
        minSize: theme.spacing.formGap.xs,
        maxSize: theme.spacing.formGap.xl,
        minVW: theme.breakpointValues.xs,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.xs,
        baseFontMax: theme.baseFont.xl,
      })};
  }
`;

export const GridItemForm = styled.div`
  grid-column: ${({ $spanFull }) => ($spanFull ? "1 / -1" : "auto")};
  grid-area: ${({ area }) => area || "auto"}; // add area prop
  min-width: 0;
  width: 100%;
`;

export const FormTitle = styled.div`
  ${TypoLabelStyles};
  font-family: ${({ theme }) => theme.fontFace.itc_avant_garde_gothicbold};
  text-transform: capitalize;
  padding-top: ${({ theme }) =>
    fluid({
      minSize: theme.spacing.formGap.xs,
      maxSize: theme.spacing.formGap.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })};
`;

export const TextFieldWrapper = styled.div`
  position: relative;
  input,
  select {
    border-radius: 9999em;
    height: ${({ theme }) =>
      fluid({
        minSize: theme.spacing.formField.xs,
        maxSize: theme.spacing.formField.xl,
        minVW: theme.breakpointValues.xs,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.xs,
        baseFontMax: theme.baseFont.xl,
      })};
  }
  select {
    background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23333%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
    background-repeat: no-repeat;
    background-position: right 1.5rem top 50%;
    background-size: 0.65rem auto;
  }
  textarea {
    border-radius: 24px;
    resize: none;
    width: 100%; /* ensure it fills the container width */
    min-width: 100%; /* prevent shrinking */
    max-width: 100%; /* prevent growing */
  }
  input,
  textarea,
  select {
    ${TypoBodyStyles}
    width: 100%;
    /* height: 100%; */
    appearance: none;
    -webkit-appearance: none; /* Safari fix */
    background-color: transparent;
    border: 1px solid ${({ theme }) => theme.colors.onyx};
    box-sizing: border-box;
    line-height: ${({ theme }) => theme.lineHeight.button}rem;
    padding: ${({ theme }) => `
      calc(${fluid({
        minSize: theme.spacing.formLabel.xs,
        maxSize: theme.spacing.formLabel.xl,
        minVW: theme.breakpointValues.xs,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.xs,
        baseFontMax: theme.baseFont.xl,
      })} / 2.5)
      ${fluid({
        minSize: theme.spacing.formLabel.xs,
        maxSize: theme.spacing.formLabel.xl,
        minVW: theme.breakpointValues.xs,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.xs,
        baseFontMax: theme.baseFont.xl,
      })}
    `};
    &:focus {
      outline: none;
      border: 1px solid ${({ theme }) => theme.colors.imperialRed};
    }
  }
  label {
    position: absolute;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    pointer-events: none;
    left: ${({ theme }) =>
      fluid({
        minSize: theme.spacing.formLabel.xs,
        maxSize: theme.spacing.formLabel.xl,
        minVW: theme.breakpointValues.xs,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.xs,
        baseFontMax: theme.baseFont.xl,
      })};
  }
  /* textarea-specific label */
  textarea + label {
    bottom: auto; /* remove stretching to match textarea height */
    align-items: flex-start;
    padding-top: 8px; /* small offset from top */
  }
`;

export const FormMessageStyle = styled.div`
  ${TypoCaptionStyles};
  font-family: ${({ theme }) => theme.fontFace.maitree};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme, success }) =>
    success ? theme.colors.emerald : theme.colors.imperialRed};
`;
